import _ from 'lodash';
import { fromJsonArrayWith } from 'src/core/utils';
import CustomFieldRequestDTO from 'src/entities/cart/request/CustomFieldRequestDTO';
import OrderProduct from 'src/entities/cart/response/OrderProduct';
import { CustomerOrderProduct } from 'src/entities/products/CustomerOrderProduct';

export default class SelectedProduct {
    ffProductNumber: string;
    quantity: number;
    nettPrice: number;
    chosenVariants: SelectedProduct[];
    childProducts: SelectedProduct[];
    customFields?: CustomFieldRequestDTO[];
    isOrderDirectly: boolean;
    name: any;
    currencyIso: string;
    vendorId: number;
    /** R25-6: the used machine (logistic piece) this line sells; undefined = new goods. */
    usedUnitId?: string;

    constructor(
        ffProductNumber: string,
        quantity: number,
        nettPrice: number,
        vendorId: number | string,
        currencyIso?: string,
        chosenVariants?: SelectedProduct[],
        childProducts?: SelectedProduct[],
        customFields?: CustomFieldRequestDTO[],
        isOrderDirectly?: boolean,
        usedUnitId?: string
    ) {
        this.ffProductNumber = ffProductNumber.toString();
        this.quantity = quantity ? Number(quantity) : quantity;
        this.nettPrice = nettPrice;
        this.vendorId = Number(vendorId);
        this.chosenVariants = chosenVariants || [];
        this.childProducts = childProducts || [];
        this.customFields = customFields || [];
        this.isOrderDirectly = isOrderDirectly ?? false;
        this.currencyIso = currencyIso || 'EUR';
        this.usedUnitId = usedUnitId || undefined;
    }

    public equals(other: SelectedProduct): boolean {
        if (this.ffProductNumber !== other.ffProductNumber) return false;
        if (this.vendorId !== other.vendorId) return false;
        if (!_.isEqual(this.customFields, other.customFields)) return false;
        if ((this.usedUnitId ?? undefined) !== (other.usedUnitId ?? undefined)) return false;
        return _.isEqual(this.chosenVariants, other.chosenVariants);
    }

    public retrieveId(): string {
        return this.vendorId != null ? `${this.ffProductNumber}_${this.vendorId}` : this.ffProductNumber;
    }

    public static fromJson(json: any): SelectedProduct {
        return new SelectedProduct(
            json.ffProductNumber,
            Number(json.quantity),
            Number(json.nettPrice),
            json.vendorId,
            'EUR',
            fromJsonArrayWith(SelectedProduct.fromJson, json.chosenVariants),
            fromJsonArrayWith(SelectedProduct.fromJson, json.childProducts),
            fromJsonArrayWith(CustomFieldRequestDTO.fromJson, json.customFields),
            undefined,
            json.usedUnitId ?? undefined
        );
    }

    public static fromOrderProduct(product: OrderProduct): SelectedProduct {
        return new SelectedProduct(
            product.ffProductNumber,
            Number(product.quantity),
            Number(product.nettPrice),
            product.vendorId,
            'EUR',
            product.chosenVariants,
            fromJsonArrayWith(SelectedProduct.fromJson, product.childProducts),
            CustomFieldRequestDTO.getCustomFieldsForRequest(product),
            undefined,
            product.usedUnitId
        );
    }

    public static fromCustomerOrderProduct(product: CustomerOrderProduct): SelectedProduct {
        return new SelectedProduct(
            product.ffProductNumber.toString(),
            Number(product.quantity),
            Number(product.nettPrice),
            product.vendorId,
            'EUR',
            undefined,
            undefined,
            CustomFieldRequestDTO.getCustomFieldsForRequest(product)
        );
    }

    public static fromProductsSelectForm(products: any[]): SelectedProduct[] {
        // All products which have a quantity greater than 0
        const productsWithQuantity = products
            .filter(p => p.ffProductNumber)
            .filter(p => p.quantity)
            .map(p => {
                p.quantity = Number(p.quantity);
                return p;
            })
            .filter(p => p.quantity > 0);

        // Construct it to a SelectedProduct
        return productsWithQuantity.map(product => {
            const { ffProductNumber, quantity, nettPrice, vendorId, usedUnitId } = product;

            const chosenVariants = SelectedProduct.fromProductsSelectForm((product.chosenVariants ?? []).flat());
            const customFields = CustomFieldRequestDTO.getCustomFieldsForRequest(product);

            return new SelectedProduct(
                ffProductNumber,
                quantity,
                nettPrice,
                vendorId,
                'EUR',
                chosenVariants,
                undefined,
                customFields,
                undefined,
                usedUnitId || undefined
            );
        });
    }

    public flattenNestedChosenVariants(): SelectedProduct {
        return new SelectedProduct(
            this.ffProductNumber,
            this.quantity,
            this.nettPrice,
            this.vendorId,
            'EUR',
            this.chosenVariants.length > 0
                ? this.flatChosenVariantList().filter(cv => cv.chosenVariants.length === 0)
                : this.chosenVariants,
            undefined,
            this.customFields,
            this.isOrderDirectly,
            this.usedUnitId
        );
    }

    private flatChosenVariantList(): SelectedProduct[] {
        if (this.chosenVariants.length === 0) return [this];
        return this.chosenVariants.map(chosenVariant => chosenVariant.flatChosenVariantList()).flat();
    }
}
